import React, { useState, useContext, useRef, useEffect } from 'react';
import { Container, Row, Col, Button, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaInbox, FaPaperPlane, FaPen, FaArrowLeft, FaReply } from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import MessageList from './components/MessageList';
import MessageDetail from './components/MessageDetail';
import MessageCompose from './components/MessageCompose';
import messageService from '../../services/messageService';

const Messages = () => {
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [showCompose, setShowCompose] = useState(false);
    const [activeTab, setActiveTab] = useState('inbox');
    const [isCompact, setIsCompact] = useState(false);
    const [showMobileDetail, setShowMobileDetail] = useState(false);
    const { user, loading } = useContext(AuthContext);
    const containerRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    // Handle URL parameters for direct message opening
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const messageId = params.get('id');
        
        if (messageId) {
            const fetchMessage = async () => {
                try {
                    const message = await messageService.getById(messageId);
                    if (message) {
                        setSelectedMessage(message);
                        setShowMobileDetail(true);
                        // Set the appropriate tab based on the message type
                        setActiveTab(message.senderId === user.id ? 'sent' : 'inbox');
                        // Clear the URL parameter without triggering a page reload
                        navigate('/user-dashboard/messages', { replace: true });
                    }
                } catch (error) {
                    console.error('Error fetching message:', error);
                }
            };
            fetchMessage();
        }
    }, [location.search, user.id, navigate]);

    useEffect(() => {
        if (!containerRef.current) return;
        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                setIsCompact(entry.contentRect.width < 300);
            }
        });
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, []);

    const handleMessageSelect = (message) => {
        setSelectedMessage(message);
        setShowMobileDetail(true);
    };

    const handleBackClick = () => {
        setShowMobileDetail(false);
        setSelectedMessage(null);
    };

    const handleComposeClick = () => {
        setShowCompose(true);
        setSelectedMessage(null); // Clear any selected message when composing new
    };

    const handleReplyClick = () => {
        setShowCompose(true);
        // Keep selectedMessage set so it can be used for reply
    };

    const handleComposeClose = () => {
        setShowCompose(false);
    };

    const handleMessageSent = (message) => {
        // Handle the sent message (e.g., refresh list, show success message)
        setShowCompose(false);
        if (showMobileDetail) {
            setShowMobileDetail(false);
        }
        setSelectedMessage(null);
        setActiveTab('sent'); // Switch to sent messages tab
    };

    const renderButton = (text, icon, onClick, variant, tooltip) => (
        <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip>{tooltip}</Tooltip>}
        >
            <Button
                variant={variant}
                onClick={onClick}
                className="d-flex align-items-center"
                style={{ height: isCompact ? '24px' : '32px' }}
            >
                {icon}
                {!isCompact && <span className="ms-2">{text}</span>}
            </Button>
        </OverlayTrigger>
    );

    return (
        <Container fluid className="mt-3">
            <Row>
                <Col md={4} ref={containerRef} className={`${showMobileDetail ? 'd-none d-md-block' : ''}`}>
                    <div className="d-flex gap-2 mb-2">
                        <div className="btn-group">
                            {renderButton(
                                'Inbox',
                                <FaInbox />,
                                () => setActiveTab('inbox'),
                                activeTab === 'inbox' ? 'primary' : 'outline-primary',
                                'View Inbox'
                            )}
                            {renderButton(
                                'Sent',
                                <FaPaperPlane />,
                                () => setActiveTab('sent'),
                                activeTab === 'sent' ? 'primary' : 'outline-primary',
                                'View Sent Messages'
                            )}
                        </div>
                        <div className="ms-auto">
                            {renderButton(
                                'Compose',
                                <FaPen />,
                                handleComposeClick,
                                'success',
                                'Compose New Message'
                            )}
                        </div>
                    </div>
                    <MessageList
                        type={activeTab}
                        onMessageSelect={handleMessageSelect}
                        userId={user.id}
                    />
                </Col>
                <Col md={8} className={`${!showMobileDetail ? 'd-none d-md-block' : ''}`}>
                    {selectedMessage ? (
                        <>
                            <div className="d-flex align-items-center justify-content-between mb-2 sticky-top">
                                <div className="d-md-none">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="d-inline-flex align-items-center"
                                        onClick={handleBackClick}
                                    >
                                        <FaArrowLeft className="me-1" /> Back
                                    </Button>
                                </div>
                                <div className="ms-auto">
                                    {renderButton(
                                        'Reply',
                                        <FaReply />,
                                        handleReplyClick,
                                        'primary',
                                        'Reply to Message'
                                    )}
                                </div>
                            </div>
                            <MessageDetail
                                message={selectedMessage}
                                userId={user.id}
                            />
                        </>
                    ) : (
                        <div className="text-center text-muted mt-5 d-none d-md-block">
                            Select a message to view its details
                        </div>
                    )}
                </Col>
            </Row>

            <MessageCompose
                show={showCompose}
                onHide={handleComposeClose}
                replyTo={selectedMessage}
                onMessageSent={handleMessageSent}
            />
        </Container>
    );
};

export default Messages;